import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Chip, ListItemIcon, ListItemText, Typography } from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SchoolIcon from '@mui/icons-material/School';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { AgentResultItem, AgentResultList } from './Agent.styles';
import { getAgentResultPath, normalizeAgentResults } from './agentResults';

const KIND_LABELS = {
  assessment: 'Assessment',
  'assessment-template': 'Assessment Template',
  task: 'Task',
  edition: 'Edition',
  course: 'Course',
  document: 'Document',
  'course-document': 'Course Document',
};

const KIND_ICONS = {
  assessment: AssignmentIcon,
  'assessment-template': AssignmentIcon,
  task: TaskAltIcon,
  edition: MenuBookIcon,
  course: SchoolIcon,
  document: ArticleIcon,
  'course-document': ArticleIcon,
};

const STATUS_LABELS = {
  published: 'Final',
  draft: 'Draft',
  review: 'Review',
  active: 'Active',
  archived: 'Archived',
};

const formatLabel = (value) =>
  `${value || ''}`
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatStatus = (status) => STATUS_LABELS[status] || formatLabel(status);

const metadataEntries = (metadata = {}) =>
  Object.entries(metadata)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .slice(0, 4)
    .map(([key, value]) => [formatLabel(key), Array.isArray(value) ? value.join(', ') : `${value}`]);

function AgentResultCard({ result }) {
  const Icon = KIND_ICONS[result.kind] || ArticleIcon;
  const path = getAgentResultPath(result);
  const isFrontendRoute = !!path;
  const linkProps = isFrontendRoute ? { component: Link, to: path } : result.url ? { component: 'a', href: result.url } : {};
  const status = formatStatus(result.status);
  const meta = metadataEntries(result.metadata);

  return (
    <AgentResultItem {...linkProps} data-testid={`agent-result-${result.kind}-${result.id || result.title}`}>
      <ListItemIcon sx={{ minWidth: 32, color: 'primary.main', paddingTop: 0.25 }}>
        <Icon fontSize="small" />
      </ListItemIcon>
      <ListItemText
        sx={{ minWidth: 0, margin: 0 }}
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Typography component="span" variant="subtitle2" noWrap sx={{ minWidth: 0 }}>
              {result.title}
            </Typography>
            <Chip size="small" label={KIND_LABELS[result.kind] || formatLabel(result.kind)} />
            {status && <Chip size="small" variant="outlined" label={status} />}
          </Box>
        }
        slotProps={{ secondary: { component: 'div' } }}
        secondary={
          <>
            {result.subtitle && (
              <Typography variant="body2" color="textSecondary" noWrap>
                {result.subtitle}
              </Typography>
            )}
            {meta.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, marginTop: 0.75 }}>
                {meta.map(([key, value]) => (
                  <Chip key={key} size="small" variant="outlined" label={`${key}: ${value}`} />
                ))}
              </Box>
            )}
          </>
        }
      />
      {isFrontendRoute ? <ChevronRightIcon color="action" fontSize="small" /> : result.url ? <OpenInNewIcon color="action" fontSize="small" /> : null}
    </AgentResultItem>
  );
}

export default function AgentResultCards({ results }) {
  const normalizedResults = normalizeAgentResults(results);
  if (!normalizedResults.length) return null;

  return (
    <AgentResultList data-testid="agent-result-list">
      {normalizedResults.map((result, index) => (
        <AgentResultCard key={`${result.kind}-${result.id || result.title}-${index}`} result={result} />
      ))}
    </AgentResultList>
  );
}

